import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UploadCloud, X, Play, Plus, AlertCircle, Check, Loader2, FileText, Scale, Shield, Eye, Settings2, ZoomIn, ZoomOut } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { projectsApi, filesApi, clausesApi } from '../services/api';
import { ExemptionType, UploadType } from '../types';
import { FILE_LIMITS } from '../constants';
import { Document, Page, pdfjs } from 'react-pdf';
import { renderAsync } from 'docx-preview';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// 设置 PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// 紧凑型文件上传组件
interface CompactFileDropzoneProps {
  label: string;
  file: File | null;
  onDrop: (file: File) => void;
  onRemove: () => void;
  required?: boolean;
  isUploading?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
}

const CompactFileDropzone: React.FC<CompactFileDropzoneProps> = ({ 
  label, file, onDrop, onRemove, required, isUploading, disabled, icon
}) => {
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (disabled || !e.dataTransfer.files?.[0]) return;
    const f = e.dataTransfer.files[0];
    if (f.size > FILE_LIMITS.MAX_SIZE_MB * 1024 * 1024) { alert(`文件不能超过 ${FILE_LIMITS.MAX_SIZE_MB}MB`); return; }
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    if (!FILE_LIMITS.ALLOWED_EXTENSIONS.includes(ext)) { alert(`只支持: ${FILE_LIMITS.ALLOWED_EXTENSIONS.join(', ')}`); return; }
    onDrop(f);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > FILE_LIMITS.MAX_SIZE_MB * 1024 * 1024) { alert(`文件不能超过 ${FILE_LIMITS.MAX_SIZE_MB}MB`); return; }
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    if (!FILE_LIMITS.ALLOWED_EXTENSIONS.includes(ext)) { alert(`只支持: ${FILE_LIMITS.ALLOWED_EXTENSIONS.join(', ')}`); return; }
    onDrop(f);
  };

  return (
    <div className="group">
      {!file ? (
        <div onDragOver={handleDragOver} onDrop={handleDrop}
          onClick={() => !disabled && document.getElementById(`file-${label}`)?.click()}
          className={`flex items-center gap-3 p-3 rounded-lg border-2 border-dashed transition-all ${
            disabled ? 'opacity-50 cursor-not-allowed border-slate-200' : 'cursor-pointer border-slate-200 hover:border-blue-400 hover:bg-blue-50/50'
          }`}>
          <input id={`file-${label}`} type="file" className="hidden" accept=".pdf,.docx,.doc" onChange={handleChange} disabled={disabled} />
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-500 transition-colors">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700">{label} {required && <span className="text-rose-500">*</span>}</p>
            <p className="text-xs text-slate-400">点击上传</p>
          </div>
          <UploadCloud size={16} className="text-slate-300 group-hover:text-blue-400" />
        </div>
      ) : (
        <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${isUploading ? 'border-blue-300 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}`}>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isUploading ? 'bg-blue-100 text-blue-500' : 'bg-emerald-100 text-emerald-500'}`}>
            {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700 truncate">{file.name}</p>
            <p className="text-xs text-slate-400">{isUploading ? '上传中...' : `${(file.size/1024).toFixed(1)} KB`}</p>
          </div>
          {!isUploading && !disabled && (
            <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-1.5 rounded-full hover:bg-white/80 text-slate-400 hover:text-rose-500">
              <X size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// 文档预览组件 - 支持 PDF 和 Word 原生预览
interface DocPreviewProps {
  file: File | null;
  label: string;
}

const DocPreview: React.FC<DocPreviewProps> = ({ file, label }) => {
  const wordContainerRef = useRef<HTMLDivElement>(null);
  const [pdfNumPages, setPdfNumPages] = useState<number>(0);
  const [pdfScale, setPdfScale] = useState<number>(1.0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 处理 Word 文件
  useEffect(() => {
    if (!file || !wordContainerRef.current) {
      return;
    }

    const isWord = file.name.endsWith('.docx') || file.name.endsWith('.doc');
    if (!isWord) return;

    setIsLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (wordContainerRef.current) {
          // 清空容器
          wordContainerRef.current.innerHTML = '';
          // 使用 docx-preview 渲染
          await renderAsync(arrayBuffer, wordContainerRef.current, undefined, {
            className: 'docx-preview-wrapper',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            ignoreLastRenderedPageBreak: true,
            experimental: false,
            trimXmlDeclaration: true,
            useBase64URL: true,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            renderEndnotes: true,
          });
        }
      } catch (err) {
        console.error('Word parsing error:', err);
        setError('Word 文档解析失败，请确保文件格式正确');
      } finally {
        setIsLoading(false);
      }
    };
    reader.onerror = () => {
      setError('文件读取失败');
      setIsLoading(false);
    };
    reader.readAsArrayBuffer(file);
  }, [file]);

  // PDF 加载成功回调
  const onPdfLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setPdfNumPages(numPages);
    setIsLoading(false);
  }, []);

  const onPdfLoadError = useCallback((err: Error) => {
    console.error('PDF loading error:', err);
    setError('PDF 加载失败');
    setIsLoading(false);
  }, []);

  // 空状态
  if (!file) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-400">
        <FileText size={48} strokeWidth={1} className="mb-3 opacity-50" />
        <p className="text-sm">请先上传{label}</p>
        <p className="text-xs mt-1">支持 PDF、Word 格式</p>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-400">
        <AlertCircle size={32} className="mb-3 text-red-400" />
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  const isPdf = file.name.endsWith('.pdf');
  const isWord = file.name.endsWith('.docx') || file.name.endsWith('.doc');

  // PDF 预览
  if (isPdf) {
    return (
      <div className="h-full flex flex-col">
        {/* PDF 工具栏 */}
        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200 rounded-t-lg">
          <span className="text-xs text-slate-500">
            {pdfNumPages > 0 ? `共 ${pdfNumPages} 页` : '加载中...'}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPdfScale(s => Math.max(0.5, s - 0.1))}
              className="p-1 hover:bg-slate-200 rounded"
              title="缩小"
            >
              <ZoomOut size={16} className="text-slate-600" />
            </button>
            <span className="text-xs text-slate-600 w-12 text-center">{Math.round(pdfScale * 100)}%</span>
            <button
              onClick={() => setPdfScale(s => Math.min(2, s + 0.1))}
              className="p-1 hover:bg-slate-200 rounded"
              title="放大"
            >
              <ZoomIn size={16} className="text-slate-600" />
            </button>
          </div>
        </div>
        {/* PDF 内容 */}
        <div className="flex-1 overflow-auto bg-slate-100 p-4">
          <Document
            file={file}
            onLoadSuccess={onPdfLoadSuccess}
            onLoadError={onPdfLoadError}
            loading={
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-blue-500" />
              </div>
            }
          >
            {Array.from(new Array(pdfNumPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                scale={pdfScale}
                className="mb-4 shadow-lg mx-auto"
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            ))}
          </Document>
        </div>
      </div>
    );
  }

  // Word 预览
  if (isWord) {
    return (
      <div className="h-full flex flex-col relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <Loader2 size={32} className="animate-spin text-blue-500" />
          </div>
        )}
        <div 
          ref={wordContainerRef} 
          className="flex-1 overflow-auto bg-slate-50"
          style={{ 
            padding: '16px',
          }}
        />
        <style>{`
          .docx-preview-wrapper {
            background: transparent;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .docx-preview-wrapper > section.docx {
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
            margin: 0 auto 16px auto;
            padding: 30px 40px;
            background: white;
            transform: scale(0.75);
            transform-origin: top center;
            max-width: 100%;
          }
          .docx-preview-wrapper table {
            border-collapse: collapse;
            width: 100%;
          }
          .docx-preview-wrapper td, .docx-preview-wrapper th {
            border: 1px solid #ddd;
            padding: 6px;
          }
          .docx-preview-wrapper p {
            margin: 0.5em 0;
          }
        `}</style>
      </div>
    );
  }

  // 默认状态
  return (
    <div className="h-full flex flex-col items-center justify-center text-slate-400">
      <FileText size={48} strokeWidth={1} className="mb-3 opacity-50" />
      <p className="text-sm">不支持的文件格式</p>
    </div>
  );
};

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const { currentProject, createProject, loadProject, uploadFile, updateProject, addExemption,
    startSplit, pollSplitStatus, startReview, pollReviewStatus, generateReport, loadReport, 
    createExport, pollExportStatus, reviewProcess, setReviewProcess, resetReviewProcess, loadClauses } = useApp();
  
  const [files, setFiles] = useState<{ contract: File | null; standard: File | null; exception: File | null }>({ contract: null, standard: null, exception: null });
  // 存储已上传文件的信息（用于显示已有文件）
  const [uploadedFiles, setUploadedFiles] = useState<{ 
    contract: { id: string; name: string; url: string } | null; 
    standard: { id: string; name: string; url: string } | null; 
    exception: { id: string; name: string; url: string } | null;
  }>({ contract: null, standard: null, exception: null });
  const [uploadStatus, setUploadStatus] = useState<{ contract: { uploading: boolean }; standard: { uploading: boolean }; exception: { uploading: boolean } }>({
    contract: { uploading: false }, standard: { uploading: false }, exception: { uploading: false }
  });
  const [prompt, setPrompt] = useState('');
  const [exemptionInput, setExemptionInput] = useState('');
  const [exemptions, setExemptions] = useState<Array<{ type: ExemptionType; value: string }>>([]);
  const [previewTab, setPreviewTab] = useState<'contract' | 'standard' | 'exception'>('contract');
  const [loadingFiles, setLoadingFiles] = useState(false);

  // 加载项目和已上传的文件
  useEffect(() => { 
    if (projectId && projectId !== 'new') {
      loadProject(projectId);
      loadUploadedFiles(projectId);
    } else if (projectId === 'new') {
      // 重置所有本地状态
      setFiles({ contract: null, standard: null, exception: null });
      setUploadedFiles({ contract: null, standard: null, exception: null });
      setUploadStatus({ contract: { uploading: false }, standard: { uploading: false }, exception: { uploading: false } });
      setPrompt('');
      setExemptionInput('');
      setExemptions([]);
      setPreviewTab('contract');
    }
  }, [projectId]);
  
  useEffect(() => { if (currentProject?.user_prompt) setPrompt(currentProject.user_prompt); }, [currentProject]);

  // 从后端加载已上传的文件列表
  const loadUploadedFiles = async (pid: string) => {
    setLoadingFiles(true);
    try {
      const fileList = await filesApi.list(pid);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
      
      const newUploadedFiles: typeof uploadedFiles = { contract: null, standard: null, exception: null };
      
      for (const file of fileList) {
        const fileInfo = {
          id: file.id,
          name: file.original_name,
          url: `${baseUrl}/projects/${pid}/files/${file.id}/download`,
        };
        
        if (file.file_type === 'contract') {
          newUploadedFiles.contract = fileInfo;
        } else if (file.file_type === 'standard') {
          newUploadedFiles.standard = fileInfo;
        } else if (file.file_type === 'exception') {
          newUploadedFiles.exception = fileInfo;
        }
      }
      
      setUploadedFiles(newUploadedFiles);
      
      // 如果有已上传的文件，自动下载并设置到 files 状态以便预览
      for (const type of ['contract', 'standard', 'exception'] as const) {
        const uploadedFile = newUploadedFiles[type];
        if (uploadedFile && !files[type]) {
          try {
            const response = await fetch(uploadedFile.url, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              },
            });
            if (response.ok) {
              const blob = await response.blob();
              const file = new File([blob], uploadedFile.name, { type: blob.type });
              setFiles(prev => ({ ...prev, [type]: file }));
            }
          } catch (e) {
            console.error(`Failed to load ${type} file:`, e);
          }
        }
      }
    } catch (e) {
      console.error('Failed to load uploaded files:', e);
    }
    setLoadingFiles(false);
  };

  const handleFileChange = async (type: UploadType, file: File) => {
    setFiles(prev => ({ ...prev, [type]: file }));
    setPreviewTab(type);
    
    if (currentProject) {
      setUploadStatus(prev => ({ ...prev, [type]: { uploading: true } }));
      try {
        await uploadFile(currentProject.id, file, type);
      } catch (e) {
        setFiles(prev => ({ ...prev, [type]: null }));
      }
      setUploadStatus(prev => ({ ...prev, [type]: { uploading: false } }));
    }
  };

  const handleRemoveFile = (type: UploadType) => {
    setFiles(prev => ({ ...prev, [type]: null }));
  };
  const handleAddExemption = () => { if (exemptionInput.trim()) { setExemptions(prev => [...prev, { type: ExemptionType.KEYWORD, value: exemptionInput.trim() }]); setExemptionInput(''); } };

  const canStart = files.contract && files.standard && !uploadStatus.contract.uploading && !uploadStatus.standard.uploading && reviewProcess.currentStep === 'idle';
  const isProcessing = reviewProcess.currentStep !== 'idle' && reviewProcess.currentStep !== 'completed' && reviewProcess.currentStep !== 'failed';

  const poll = async <T,>(fn: () => Promise<T>, check: (r: T) => boolean, interval = 2000, max = 300): Promise<T> => {
    let i = 0;
    while (i < max) { const r = await fn(); if (check(r)) return r; await new Promise(res => setTimeout(res, interval)); i++; }
    throw new Error('操作超时');
  };


  const handleStartReview = async () => {
    setReviewProcess({ errorMessage: null });
    try {
      let pid = currentProject?.id;
      setReviewProcess({ currentStep: 'uploading', stepProgress: 0 });
      
      if (!pid || projectId === 'new') {
        const p = await createProject(files.contract?.name || '新合同审核', undefined, prompt || undefined);
        pid = p.id;
        
        if (files.contract) await uploadFile(p.id, files.contract, 'contract');
        setReviewProcess({ stepProgress: 33 });
        
        if (files.standard) await uploadFile(p.id, files.standard, 'standard');
        setReviewProcess({ stepProgress: 66 });
        
        if (files.exception) await uploadFile(p.id, files.exception, 'exception');
        setReviewProcess({ stepProgress: 100 });
      } else {
        if (prompt !== currentProject?.user_prompt) await updateProject(pid, { user_prompt: prompt });
        setReviewProcess({ stepProgress: 100 });
      }
      
      for (const ex of exemptions) {
        await addExemption(pid, { exemption_type: ex.type, name: `关键词: ${ex.value}`, keywords: [ex.value], reason: '用户添加' });
      }
      
      setReviewProcess({ currentStep: 'splitting', stepProgress: 0 });
      await startSplit(pid);
      await poll(
        async () => {
          const status = await clausesApi.getSplitStatus(pid!);
          const prog = status.progress ?? 0;
          setReviewProcess({ stepProgress: Math.min(prog, 99) });
          if (status.status === 'completed') {
            await loadClauses(pid!);
            await loadProject(pid!);
            return true;
          }
          if (status.status === 'failed') {
            throw new Error(status.error_message || 'AI 分析失败');
          }
          return false;
        },
        c => c,
        2000,
      );
      setReviewProcess({ stepProgress: 100 });
      
      setReviewProcess({ currentStep: 'reviewing', stepProgress: 0 });
      await startReview(pid);
      await poll(() => pollReviewStatus(pid!), r => { setReviewProcess({ stepProgress: Math.max(1, Math.min(r.progress, 99)) }); return r.completed; }, 1000, 1800);
      setReviewProcess({ stepProgress: 100 });
      
      setReviewProcess({ currentStep: 'reporting', stepProgress: 0 });
      await generateReport(pid);
      let ready = false, att = 0;
      while (!ready && att < 120) {
        const st = await projectsApi.getStatus(pid);
        if (st.status === 'completed') { await loadReport(pid); ready = true; setReviewProcess({ stepProgress: 100 }); }
        else if (st.status === 'failed') throw new Error(st.error_message || '报告生成失败');
        else { att++; setReviewProcess({ stepProgress: 30 + Math.min(att, 60) }); await new Promise(r => setTimeout(r, 1000)); }
      }
      if (!ready) throw new Error('报告生成超时');
      
      await loadProject(pid);
      setReviewProcess({ currentStep: 'exporting', stepProgress: 0 });
      await new Promise(r => setTimeout(r, 1000));
      const exp = await createExport(pid, 'docx', { comparison_mode: 'dual_column', include_appendix: true, include_summary: true, user_edit_priority: true });
      await poll(() => pollExportStatus(pid!, exp.id), r => { setReviewProcess({ stepProgress: r.progress }); return r.status === 'completed' || r.status === 'failed'; }, 2000);
      
      setReviewProcess({ currentStep: 'completed', stepProgress: 100 });
      setTimeout(() => navigate(`/project/${pid}/report`), 1500);
    } catch (e: any) {
      console.error('Review failed:', e);
      setReviewProcess({ currentStep: 'failed', errorMessage: e.message || '审核出错' });
    }
  };

  const actionLabel = (() => {
    switch (reviewProcess.currentStep) {
      case 'uploading': return '正在保存文件...';
      case 'splitting': return 'AI 正在分析合同...';
      case 'reviewing': return 'AI 正在审查条款...';
      case 'reporting': return '正在生成报告...';
      case 'exporting': return '正在准备导出...';
      default: return '审核中...';
    }
  })();


  return (
    <div className="h-full flex gap-4 p-4 animate-fade-in">
      {/* 左侧：上传 + 额外审查设置 - 固定高度，内容溢出时自己滚动 */}
      <div className="w-80 flex-shrink-0 h-full flex flex-col gap-4 overflow-hidden">
        {/* 上传区域 */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex-shrink-0">
          <h2 className="text-sm font-bold text-slate-800 mb-3">上传材料</h2>
          <div className="space-y-2">
            <CompactFileDropzone label="合同文档" file={files.contract} onDrop={f => handleFileChange('contract', f)} onRemove={() => handleRemoveFile('contract')} required isUploading={uploadStatus.contract.uploading} disabled={isProcessing} icon={<FileText size={18} />} />
            <CompactFileDropzone label="审查标准" file={files.standard} onDrop={f => handleFileChange('standard', f)} onRemove={() => handleRemoveFile('standard')} required isUploading={uploadStatus.standard.uploading} disabled={isProcessing} icon={<Scale size={18} />} />
            <CompactFileDropzone label="豁免条例" file={files.exception} onDrop={f => handleFileChange('exception', f)} onRemove={() => handleRemoveFile('exception')} isUploading={uploadStatus.exception.uploading} disabled={isProcessing} icon={<Shield size={18} />} />
          </div>
        </div>

        {/* 额外审查设置 */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex-shrink-0">
          <div className="flex items-center gap-2 mb-3">
            <Settings2 size={16} className="text-slate-600" />
            <h2 className="text-sm font-bold text-slate-800">额外审查设置</h2>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">额外要求 / 重点关注领域</label>
              <textarea 
                className="w-full text-sm border border-slate-200 rounded-lg p-2 h-20 resize-none focus:ring-1 focus:ring-slate-400 outline-none" 
                placeholder="例如：请特别关注赔偿条款。忽略格式问题。"
                value={prompt} 
                onChange={e => setPrompt(e.target.value)} 
                disabled={isProcessing} 
              />
              <p className="text-xs text-slate-400 mt-1">此提示将作为审核偏好传递给 AI</p>
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg">
              <h3 className="text-xs font-medium text-slate-700 mb-2">豁免规则</h3>
              <p className="text-xs text-slate-500 mb-2">定义需自动跳过的关键词或章节</p>
              
              <div className="flex gap-2 mb-2">
                <input 
                  type="text" 
                  className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-slate-400 outline-none" 
                  placeholder="关键词或条款标题" 
                  value={exemptionInput} 
                  onChange={e => setExemptionInput(e.target.value)} 
                  onKeyDown={e => e.key === 'Enter' && handleAddExemption()} 
                  disabled={isProcessing} 
                />
                <button onClick={handleAddExemption} disabled={isProcessing} className="flex items-center gap-1 text-sm px-3 py-1.5 bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50">
                  <Plus size={14} /> 添加
                </button>
              </div>
              
              {exemptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {exemptions.map((ex, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs bg-white border border-slate-200 px-2 py-0.5 rounded">
                      {ex.value}
                      <button onClick={() => setExemptions(p => p.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500" disabled={isProcessing}>
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* 开始按钮 */}
        <button 
          disabled={!canStart || isProcessing} 
          onClick={reviewProcess.currentStep === 'failed' ? resetReviewProcess : handleStartReview}
          className={`w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all ${
            canStart && !isProcessing ? 'bg-emerald-600 hover:bg-emerald-700 shadow-lg' : 
            reviewProcess.currentStep === 'failed' ? 'bg-slate-700 hover:bg-slate-800' : 
            'bg-slate-300 cursor-not-allowed'
          }`}
        >
          {isProcessing ? <><Loader2 size={18} className="animate-spin" /> {actionLabel}</> : 
           reviewProcess.currentStep === 'failed' ? '重新开始' : 
           <><Play size={18} /> 开始审核</>}
        </button>
        {reviewProcess.currentStep === 'failed' && reviewProcess.errorMessage && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
            {reviewProcess.errorMessage}
          </div>
        )}
      </div>


      {/* 右侧：文档预览 */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
        {/* Tab 切换 */}
        <div className="flex items-center border-b border-slate-100 px-4 py-2 gap-1 bg-slate-50/50">
          <button onClick={() => setPreviewTab('contract')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              previewTab === 'contract' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}>
            <FileText size={14} /> 合同文档
            {files.contract && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
          </button>
          <button onClick={() => setPreviewTab('standard')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              previewTab === 'standard' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}>
            <Scale size={14} /> 审查标准
            {files.standard && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
          </button>
          <button onClick={() => setPreviewTab('exception')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              previewTab === 'exception' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}>
            <Shield size={14} /> 豁免条例
            {files.exception && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Eye size={12} /> 预览
          </div>
        </div>
        
        {/* 文档内容 - 让 DocPreview 自己处理滚动 */}
        <div className="flex-1 overflow-hidden">
          {loadingFiles ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400">
              <Loader2 size={32} className="animate-spin text-blue-500 mb-3" />
              <p className="text-sm">加载已上传文件...</p>
            </div>
          ) : (
            <DocPreview 
              file={previewTab === 'contract' ? files.contract : previewTab === 'standard' ? files.standard : files.exception}
              label={previewTab === 'contract' ? '合同文档' : previewTab === 'standard' ? '审查标准' : '豁免条例'}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPage;
